import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Separator } from "components/ui/separator";
import { useCampaignProject } from "hooks/useCampaignProject";
import { getAuthUserDisplayName } from "lib/authUserDisplay";
import {
  areAllRiskAcknowledgementsChecked,
  buildInvestPagePath,
  buildOfferingDocumentPath,
  parseInvestmentFlowSearchParams,
  RISK_DISCLOSURE_REQUIRED_ACK_IDS,
  validateBeforeInvest,
} from "lib/investmentHelper";
import { RISK_ACK_WITHDRAWAL_BULLET } from "lib/ni45110Copy";
import swtAlert from "lib/sweetAlert";
import { selectAuthUser } from "store/reducer";
import { OPPORTUNITIES_PATH } from "lib/opportunitiesNav";
import { cn } from "lib/utils";

/** Form 45-110F2: single acknowledgement column only (no Yes/No). */
const RISK_ACK_CHECKBOX_CLASS =
  "h-5 w-5 shrink-0 rounded-[3px] border-2 border-foreground/80 bg-background shadow-none " +
  "data-[state=checked]:bg-foreground data-[state=checked]:border-foreground " +
  "data-[state=checked]:text-background focus-visible:ring-foreground/30";

/** @returns {"all" | "some" | "none"} */
function getSelectAllCheckedState(allChecked, someChecked) {
  if (allChecked) return "all";
  if (someChecked) return "some";
  return "none";
}

function getRemainingAcknowledgementCount(acknowledged) {
  return RISK_DISCLOSURE_REQUIRED_ACK_IDS.filter((id) => !acknowledged[id]).length;
}

function RiskAckCheckbox({ className, ...props }) {
  return <Checkbox className={cn(RISK_ACK_CHECKBOX_CLASS, className)} {...props} />;
}

RiskAckCheckbox.propTypes = {
  className: PropTypes.string,
};

function MetaField({ label, value }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5 mb-4 sm:flex-nowrap">
      <span className="font-semibold text-base shrink-0">{label}</span>
      <div className="flex-1 min-w-40 text-base border-b border-foreground/70 pb-1 sm:min-w-0">
        {value || "—"}
      </div>
    </div>
  );
}

MetaField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

function SectionTitle({ children, className }) {
  return (
    <h2
      className={cn(
        "font-bold text-base sm:text-lg leading-snug mt-8 mb-4 first:mt-0",
        className
      )}
    >
      {children}
    </h2>
  );
}

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

function AcknowledgementColumnHeader() {
  return (
    <div
      className="hidden sm:grid grid-cols-[1fr_5.5rem] gap-4 items-end mb-3 pb-2 border-b border-foreground/25"
      aria-hidden
    >
      <span />
      <span className="text-sm font-semibold text-center text-foreground">Acknowledge</span>
    </div>
  );
}

function AcknowledgementToolbar({ allChecked, someChecked, onSelectAll }) {
  const selectAllState = getSelectAllCheckedState(allChecked, someChecked);
  let selectAllChecked = false;
  if (selectAllState === "all") {
    selectAllChecked = true;
  } else if (selectAllState === "some") {
    selectAllChecked = "indeterminate";
  }

  return (
    <div className="mb-6 rounded-md border border-foreground/20 bg-muted/30 px-4 py-4 sm:px-5">
      <p className="text-sm sm:text-base text-foreground leading-relaxed mb-4">
        Check each box to confirm your understanding. This form uses acknowledgement
        only—there is no &quot;No&quot; option. You must agree with every statement to
        invest.
      </p>
      <div className="flex items-center justify-end gap-3">
        <RiskAckCheckbox
          id="risk-select-all"
          checked={selectAllChecked}
          onCheckedChange={(value) => onSelectAll(value === true)}
          aria-label="Select all acknowledgements"
        />
        <Label
          htmlFor="risk-select-all"
          className="text-base font-medium cursor-pointer text-foreground"
        >
          Select all
        </Label>
      </div>
    </div>
  );
}

AcknowledgementToolbar.propTypes = {
  allChecked: PropTypes.bool.isRequired,
  someChecked: PropTypes.bool.isRequired,
  onSelectAll: PropTypes.func.isRequired,
};

function QuestionRow({ id, term, children, checked, onCheckedChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_5.5rem] gap-3 sm:gap-4 mb-5 last:mb-0 sm:items-start">
      <div className="min-w-0 text-base leading-relaxed text-foreground">
        <span className="font-semibold">{term}</span>
        {children}
      </div>
      <div className="flex sm:justify-center sm:pt-0.5">
        <RiskAckCheckbox
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-label={`Acknowledge: ${term}`}
        />
      </div>
    </div>
  );
}

QuestionRow.propTypes = {
  id: PropTypes.string.isRequired,
  term: PropTypes.string.isRequired,
  children: PropTypes.node,
  checked: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
};

function BulletRow({ id, children, checked, onCheckedChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_5.5rem] gap-3 sm:gap-4 mb-5 last:mb-0 sm:items-start">
      <Label
        htmlFor={id}
        className="flex min-w-0 gap-2 text-left items-start text-base leading-relaxed font-normal cursor-pointer text-foreground"
      >
        <span className="shrink-0 pt-0.5" aria-hidden>
          ▪
        </span>
        <span className="min-w-0 flex-1">{children}</span>
      </Label>
      <div className="flex sm:justify-center sm:pt-0.5">
        <RiskAckCheckbox
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-label="Acknowledge additional information"
        />
      </div>
    </div>
  );
}

BulletRow.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  checked: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
};

export default function RiskDisclosurePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authUser = useSelector(selectAuthUser);
  const flow = parseInvestmentFlowSearchParams(searchParams);
  const { campaignId, amount, minInvestment, maxInvestment } = flow;

  const { campaign, loading: campaignLoading } = useCampaignProject(campaignId);
  const [acknowledged, setAcknowledged] = useState({});
  const [purchaserName, setPurchaserName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const allAcksChecked = areAllRiskAcknowledgementsChecked(acknowledged);
  const someAcksChecked = RISK_DISCLOSURE_REQUIRED_ACK_IDS.some((id) =>
    Boolean(acknowledged[id])
  );
  const nameValid = purchaserName.trim().length >= 2;
  const canProceed = allAcksChecked && nameValid && !campaignLoading;
  const remainingAckCount = useMemo(
    () => getRemainingAcknowledgementCount(acknowledged),
    [acknowledged]
  );

  const formattedAmount = useMemo(() => {
    if (!Number.isFinite(amount)) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, [amount]);

  useEffect(() => {
    const prefill = getAuthUserDisplayName(authUser);
    if (prefill) {
      setPurchaserName(prefill);
    }
  }, [authUser]);

  const setAck = useCallback((id, value) => {
    setAcknowledged((prev) => ({ ...prev, [id]: value === true }));
  }, []);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setAcknowledged(
        RISK_DISCLOSURE_REQUIRED_ACK_IDS.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {})
      );
      return;
    }
    setAcknowledged({});
  }, []);

  const handleProceed = async () => {
    if (!campaignId) {
      await swtAlert.error(
        "Missing campaign",
        "Return to the campaign and select Invest again."
      );
      return;
    }

    if (!allAcksChecked) {
      await swtAlert.warning(
        "Acknowledgement required",
        "Please check all required acknowledgement boxes before proceeding."
      );
      return;
    }

    if (!nameValid) {
      setNameTouched(true);
      await swtAlert.warning(
        "Name required",
        "Enter your first and last name to continue."
      );
      return;
    }

    const preflight = validateBeforeInvest({
      projectId: campaignId,
      amount,
      minInvestment: minInvestment || campaign?.minInvestment,
      maxInvestment: maxInvestment ?? campaign?.maxInvestment,
      campaignStartAt: campaign?.campaignStartAt,
      campaignEndAt: campaign?.campaignEndAt,
      daysRemaining: campaign?.daysRemaining,
      status: campaign?.status,
      canInvest: campaign?.canInvest,
    });

    if (!preflight.ok) {
      if (preflight.severity === "error") {
        await swtAlert.error(preflight.title, preflight.message);
      } else {
        await swtAlert.warning(preflight.title, preflight.message);
      }
      return;
    }

    navigate(
      buildInvestPagePath(
        campaignId,
        amount,
        minInvestment || campaign?.minInvestment,
        maxInvestment ?? campaign?.maxInvestment
      )
    );
  };

  const issuerName = String(campaign?.title ?? "").trim() || String(campaign?.company?.name ?? "").trim() || "";
  const securityType = String(campaign?.typeOfEligibleSecurityOffered ?? "").trim() || String(campaign?.category ?? "").trim() || "";

  if (!campaignId) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-lg text-center">
          <h1 className="text-2xl font-semibold">Risk acknowledgement</h1>
          <p className="mt-4 text-muted-foreground">
            Start investing from a campaign page to complete this form.
          </p>
          <Button
            variant="outline"
            className="d-none mt-8"
            asChild
          >
            <Link to={OPPORTUNITIES_PATH}>Browse opportunities</Link>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <>
      <Helmet>
        <title>Form 45-110F2 – Risk acknowledgement | Fortify</title>
        <meta
          name="description"
          content="National Instrument 45-110 risk acknowledgement form for start-up crowdfunding."
        />
      </Helmet>

      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 max-w-5xl">
          <Link
            to={buildOfferingDocumentPath(
              campaignId,
              amount,
              minInvestment,
              maxInvestment
            )}
            className="inline-flex items-center gap-1.5 text-base text-accent font-medium hover:underline mb-8"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to offering document
          </Link>

          <div className="mb-10">
            <p className="text-sm sm:text-base text-muted-foreground uppercase tracking-wide">
              Step 2 of 3 · Legal
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-2">
              Risk acknowledgement
            </h1>
            <p className="mt-3 text-muted-foreground text-base max-w-2xl">
              Form 45-110F2. Read carefully before investing.
            </p>
            {formattedAmount ? (
              <p className="mt-3 text-base font-medium tabular-nums">
                Investment amount: {formattedAmount}
              </p>
            ) : null}
          </div>

          <Card className="mb-10 shadow-md print:shadow-none print:border print:rounded-none">
            <CardContent className="pt-10 pb-20 px-6 sm:px-10 sm:pt-12 sm:pb-24 relative">
              <div className="text-center font-bold text-base sm:text-lg mb-1.5">
                FORM 45-110F2
              </div>
              <div className="text-center font-bold text-lg sm:text-xl tracking-wide mb-10">
                RISK ACKNOWLEDGEMENT
              </div>

              <MetaField label="Issuer Name:" value={issuerName} />
              <MetaField label="Type of Eligible Security offered:" value={securityType} />

              <div
                role="note"
                className="border-2 border-destructive rounded-md bg-destructive/5 px-5 py-5 sm:px-6 sm:py-6 text-center my-8"
              >
                <div className="font-bold text-xl sm:text-2xl text-destructive">WARNING!</div>
                <div className="font-bold text-base sm:text-lg text-destructive mt-2">
                  BUYER BEWARE: This investment is risky.
                </div>
                <p className="text-base leading-relaxed mt-3 text-foreground">
                  Don&apos;t invest unless you can afford to lose all{" "}
                  <strong className="text-destructive">
                    the money you pay for this investment.
                  </strong>
                </p>
              </div>

              <AcknowledgementToolbar
                allChecked={allAcksChecked}
                someChecked={someAcksChecked}
                onSelectAll={handleSelectAll}
              />

              <SectionTitle>1. Risk acknowledgment</SectionTitle>
              <AcknowledgementColumnHeader />

              <QuestionRow
                id="risk-q1"
                term="Risk of loss"
                checked={Boolean(acknowledged["risk-q1"])}
                onCheckedChange={(v) => setAck("risk-q1", v)}
              >
                {" "}
                – Do you understand that this is a risky investment and that you may lose
                all the money you pay for this investment?
              </QuestionRow>
              <QuestionRow
                id="risk-q2"
                term="No income"
                checked={Boolean(acknowledged["risk-q2"])}
                onCheckedChange={(v) => setAck("risk-q2", v)}
              >
                {" "}
                – Do you understand that you may not earn any income, such as dividends or
                interest, on this investment?
              </QuestionRow>
              <QuestionRow
                id="risk-q3"
                term="Liquidity risk"
                checked={Boolean(acknowledged["risk-q3"])}
                onCheckedChange={(v) => setAck("risk-q3", v)}
              >
                {" "}
                – Do you understand that you may never be able to sell this investment?
              </QuestionRow>
              <QuestionRow
                id="risk-q4"
                term="Lack of information"
                checked={Boolean(acknowledged["risk-q4"])}
                onCheckedChange={(v) => setAck("risk-q4", v)}
              >
                {" "}
                – Do you understand that you may not be provided with any ongoing information
                about the issuer and/or this investment?
              </QuestionRow>

              <SectionTitle>2. No approval and no advice</SectionTitle>
              <AcknowledgementColumnHeader />

              <QuestionRow
                id="risk-q5"
                term="No approval"
                checked={Boolean(acknowledged["risk-q5"])}
                onCheckedChange={(v) => setAck("risk-q5", v)}
              >
                {" "}
                – Do you understand that this investment has not been reviewed or approved in
                any way by a securities regulatory authority or regulator?
              </QuestionRow>
              <QuestionRow
                id="risk-q6"
                term="No advice"
                checked={Boolean(acknowledged["risk-q6"])}
                onCheckedChange={(v) => setAck("risk-q6", v)}
              >
                {" "}
                – Do you understand that you will not receive advice about your investment?
              </QuestionRow>

              <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-muted-foreground print:static print:translate-x-0 print:text-center print:mt-8 print:mb-0">
                1
              </p>
            </CardContent>
          </Card>

          <Card className="mb-10 shadow-md print:shadow-none print:border print:rounded-none">
            <CardContent className="pt-10 pb-20 px-6 sm:px-10 sm:pt-12 sm:pb-24 relative">
              <SectionTitle>3. Limited legal rights</SectionTitle>
              <AcknowledgementColumnHeader />
              <QuestionRow
                id="risk-q7"
                term="Limited legal rights"
                checked={Boolean(acknowledged["risk-q7"])}
                onCheckedChange={(v) => setAck("risk-q7", v)}
              >
                {" "}
                – Do you understand that you will not have the same rights as if you
                purchased under a prospectus or through a stock exchange?
                <br />
                <br />
                If you want to know more, you may need to seek professional legal advice.
              </QuestionRow>

              <SectionTitle>4. Purchaser&apos;s acknowledgement</SectionTitle>
              <AcknowledgementColumnHeader />
              <QuestionRow
                id="risk-q8"
                term="Investment risks"
                checked={Boolean(acknowledged["risk-q8"])}
                onCheckedChange={(v) => setAck("risk-q8", v)}
              >
                {" "}
                – Have you read this form and do you understand the risks of making this
                investment?
              </QuestionRow>
              <QuestionRow
                id="risk-q9"
                term="Offering document"
                checked={Boolean(acknowledged["risk-q9"])}
                onCheckedChange={(v) => setAck("risk-q9", v)}
              >
                {" "}
                – Has an offering document relating to this investment been made available to
                you on the funding portal?
                <br />
                <br />
                The offering document contains important information about this investment. If
                you have not read the offering document or if you do not understand the
                information in it, you should not invest. You should retain a copy of the
                offering document for your records.
                <br />
                <br />
                Have you read and do you understand the information in the offering document?
              </QuestionRow>

              <Separator className="my-10" />

              <div className="flex flex-wrap items-end gap-3 mb-3 sm:flex-nowrap">
                <Label htmlFor="purchaser-name" className="font-semibold text-base shrink-0">
                  First and last name:
                </Label>
                <Input
                  id="purchaser-name"
                  autoComplete="name"
                  value={purchaserName}
                  onChange={(e) => setPurchaserName(e.target.value)}
                  onBlur={() => setNameTouched(true)}
                  className="flex-1 min-w-0 text-base h-10 border-0 border-b rounded-none shadow-none px-0 focus-visible:ring-0 focus-visible:border-b-foreground"
                  aria-invalid={nameTouched && !nameValid}
                />
              </div>
              {nameTouched && !nameValid ? (
                <p className="text-sm text-destructive mb-6" role="alert">
                  Enter your first and last name to continue.
                </p>
              ) : (
                <div className="mb-6" />
              )}

              <p className="text-base leading-relaxed mb-8 text-foreground">
                <strong>Electronic signature:</strong> By clicking the{" "}
                <strong>I confirm</strong> button, I acknowledge that I am signing this form
                electronically and agree that this is the legal equivalent of my handwritten
                signature. I will not at any time in the future claim that my electronic
                signature is not legally binding. The date of my electronic signature is the
                same as my acknowledgement.
              </p>

              <SectionTitle>5. Additional information</SectionTitle>
              <AcknowledgementColumnHeader />

              <BulletRow
                id="risk-b1"
                checked={Boolean(acknowledged["risk-b1"])}
                onCheckedChange={(v) => setAck("risk-b1", v)}
              >
                {RISK_ACK_WITHDRAWAL_BULLET}
              </BulletRow>

              <BulletRow
                id="risk-b2"
                checked={Boolean(acknowledged["risk-b2"])}
                onCheckedChange={(v) => setAck("risk-b2", v)}
              >
                If you want more information about your local securities regulation, go to{" "}
                <a
                  href="https://www.securities-administrators.ca/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline-offset-4 hover:underline font-medium"
                >
                  www.securities-administrators.ca
                </a>
                {". Securities regulators do not provide advice on investment."}
              </BulletRow>

              <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-muted-foreground print:static print:translate-x-0 print:text-center print:mt-8 print:mb-0">
                2
              </p>
            </CardContent>
          </Card>

          {remainingAckCount > 0 ? (
            <p className="text-base text-muted-foreground mb-6 text-center">
              {remainingAckCount} acknowledgement
              {remainingAckCount === 1 ? "" : "s"} remaining
            </p>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center pb-12">
            <Button
              type="button"
              size="lg"
              className="w-full sm:w-auto min-w-48 text-base h-12 px-8"
              disabled={!canProceed}
              onClick={handleProceed}
            >
              I confirm
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
